import {
  FileInterface,
  FileRejection,
  FileStatus,
} from "fe-shared/src/types/common";

export const ALLOWED_DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".xlsx",
  ".png",
  ".jpg",
  ".jpeg",
  ".tif",
  ".tiff",
];

export const MAX_DOCUMENT_SIZE_BYTES = 200 * 1024 * 1024;

// The cap the story sets is across every file on one request, not per file. The service
// enforces it too - it is the only side that can see files already attached.
export const MAX_DOCUMENTS_TOTAL_SIZE_BYTES = 200 * 1024 * 1024;

export type DocumentRejectionReason = "extension" | "size" | "totalSize";

export interface PendingDocument {
  id: string;
  file: File;
  description: string;
}

export interface RejectedDocument {
  file: File;
  reason: DocumentRejectionReason;
}

const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");

  return dotIndex === -1 ? "" : fileName.slice(dotIndex).toLowerCase();
};

export const getDocumentRejectionReason = (
  file: File
): DocumentRejectionReason | null => {
  if (!ALLOWED_DOCUMENT_EXTENSIONS.includes(getFileExtension(file.name))) {
    return "extension";
  }

  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    return "size";
  }

  return null;
};

const nextPendingDocumentId = (() => {
  let counter = 0;

  return () => {
    counter += 1;
    return `pending-document-${counter}`;
  };
})();

// FileStatus.Loaded, not Added: FileItem reads Added as "mid-upload or failed" - it renders
// the remove button with no icon inside it and disables the description input. These files are
// settled; they are simply uploaded once the request exists rather than on drop.
export const toUploaderFiles = (documents: PendingDocument[]): FileInterface[] =>
  documents.map((document) => ({
    id: document.id,
    name: document.file.name,
    size: document.file.size,
    description: document.description,
    status: FileStatus.Loaded,
  }));

// react-dropzone filters on `accept` before onDrop runs, so a wrong-extension file never
// reaches partitionSelectedDocuments - it arrives here instead, and without this it was
// dropped with nothing shown to the user.
export const describeDropzoneRejections = (
  rejections: FileRejection[]
): RejectedDocument[] =>
  rejections.map((rejection) => ({
    file: rejection.file,
    reason: rejection.errors.some((error) => error.code === "file-too-large")
      ? "size"
      : "extension",
  }));

export interface PartitionedDocuments {
  accepted: PendingDocument[];
  rejected: RejectedDocument[];
}

// Rejected files never reach `accepted`, so no upload call is ever built for them —
// this is what keeps a disallowed extension or an over-cap file from being sent at all.
export const partitionSelectedDocuments = (
  files: File[],
  alreadyAdded: PendingDocument[] = []
): PartitionedDocuments => {
  let runningTotal = alreadyAdded.reduce(
    (total, document) => total + document.file.size,
    0
  );

  return files.reduce<PartitionedDocuments>(
    (result, file) => {
      const reason = getDocumentRejectionReason(file);

      if (reason) {
        result.rejected.push({ file, reason });
        return result;
      }

      // Counted against the files kept so far, not against the whole drop: otherwise one
      // oversized file at the front rejects the smaller ones behind it.
      if (runningTotal + file.size > MAX_DOCUMENTS_TOTAL_SIZE_BYTES) {
        result.rejected.push({ file, reason: "totalSize" });
        return result;
      }

      runningTotal += file.size;
      result.accepted.push({
        id: nextPendingDocumentId(),
        file,
        description: "",
      });

      return result;
    },
    { accepted: [], rejected: [] }
  );
};
